import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import {
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Mic,
  type LucideIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import "./canvasNewChat.css";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_MENU_GLYPH,
  CANVAS_TILE_GLYPH,
} from "./canvasToolbarComposition";

/**
 * New Chat — the floating rust disc and its hover arc (exp-canvas-toolbar
 * chat.jsx). The circle is the bar's sibling in a different shape, a split
 * button: the body fires New Chat, the disclosure fans three satellites on
 * one radius — Quick chat · Comment · Voice — after a 200ms dwell. One tab
 * stop (the circle); ArrowUp opens the fan on the keyboard and focus roves
 * the satellites; Enter and Space fire a variant and come home to the
 * circle; Escape closes. `disarmed` is the tray's takeover: while a tray is
 * docked the fan does not exist and the circle stays a live
 * click target. Coarse pointers never get the arc.
 */

export type CanvasNewChatArcKey = "quick" | "comment" | "voice";

/** One radius, three angles off vertical, clockwise positive. */
export const ARC_RADIUS = 49;
export const ARC_SEATS_DEG: Record<CanvasNewChatArcKey, number> = {
  quick: -22,
  comment: 25,
  voice: 72,
};

const seat = (deg: number) => {
  const a = (deg * Math.PI) / 180;
  return {
    dx: Math.round(ARC_RADIUS * Math.sin(a)),
    dy: Math.round(-ARC_RADIUS * Math.cos(a)),
  };
};

const ARC_ITEMS: {
  key: CanvasNewChatArcKey;
  label: string;
  icon: LucideIcon;
  k: number;
}[] = [
  { key: "quick", label: "Quick chat", icon: MessageSquare, k: 0 },
  { key: "comment", label: "Comment", icon: MessageCircle, k: 1 },
  { key: "voice", label: "Voice", icon: Mic, k: 2 },
];

const OPEN_DELAY = 200;
const CLOSE_DELAY = 150;

function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: coarse)");
    const sync = () => setCoarse(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return coarse;
}

interface CanvasNewChatProps {
  disarmed?: boolean;
  /** The chat window is up: the circle reads pressed and its click closes
   * it. The arc stays live — its placements are board verbs. */
  chatOpen?: boolean;
  /** Comment is gated on `can_comment`; its seat stays empty when false. */
  canComment?: boolean;
  onNewChat: () => void;
  onSelect: (key: CanvasNewChatArcKey) => void;
  className?: string;
  onboardingTargetId?: string;
}

export function CanvasNewChat({
  disarmed = false,
  chatOpen = false,
  canComment = true,
  onNewChat,
  onSelect,
  className,
  onboardingTargetId,
}: CanvasNewChatProps) {
  const coarse = useCoarsePointer();
  const items = ARC_ITEMS.filter((it) => canComment || it.key !== "comment");

  const [hoverOpen, setHoverOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);

  const open = !disarmed && (hoverOpen || kbOpen);

  const arcId = useId();
  const btnRef = useRef<HTMLButtonElement>(null);
  const satRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const openT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (openT.current) clearTimeout(openT.current);
    if (closeT.current) clearTimeout(closeT.current);
  };
  useEffect(() => clearTimers, []);

  const closeNow = useCallback(() => {
    clearTimers();
    setHoverOpen(false);
    setKbOpen(false);
    setFocusIndex(-1);
  }, []);

  // Disarming mid-interaction retracts whatever is out; a keyboard inside the
  // fan comes home to the circle rather than being stranded.
  useEffect(() => {
    if (!disarmed) return;
    clearTimers();
    const inside = satRefs.current.some(
      (el) => el && el === document.activeElement,
    );
    setHoverOpen(false);
    setKbOpen(false);
    setFocusIndex(-1);
    if (inside) btnRef.current?.focus();
  }, [disarmed]);

  // Roving focus: the satellites stay at tabIndex -1 — one tab stop — and
  // focus moves programmatically; hover never steals it.
  useEffect(() => {
    if (!open || focusIndex < 0) return;
    satRefs.current[focusIndex]?.focus();
  }, [open, focusIndex]);

  const onEnter = () => {
    if (coarse || disarmed) return;
    if (closeT.current) clearTimeout(closeT.current);
    if (open) return;
    if (openT.current) clearTimeout(openT.current);
    openT.current = setTimeout(() => setHoverOpen(true), OPEN_DELAY);
  };
  const onLeave = () => {
    if (coarse || disarmed) return;
    clearTimers();
    // Only the hover path closes here; a keyboard-opened fan waits for
    // Escape, Tab or focus leaving.
    closeT.current = setTimeout(() => setHoverOpen(false), CLOSE_DELAY);
  };

  const onBtnKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowUp" || (e.altKey && e.key === "ArrowDown")) {
      if (coarse || disarmed) return;
      e.preventDefault();
      clearTimers();
      setKbOpen(true);
      setFocusIndex(0);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      closeNow();
    }
    // Enter / Space fall through to the native click: focus never opens the fan.
  };

  const returnToCircle = () => {
    closeNow();
    btnRef.current?.focus();
  };

  const onSatKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = items.length;
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        setFocusIndex((i + 1) % n);
        break;
      case "ArrowLeft":
        e.preventDefault();
        setFocusIndex((i - 1 + n) % n);
        break;
      case "Home":
        e.preventDefault();
        setFocusIndex(0);
        break;
      case "End":
        e.preventDefault();
        setFocusIndex(n - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        returnToCircle();
        onSelect(items[i].key);
        break;
      case "Escape":
        e.preventDefault();
        returnToCircle();
        break;
      case "Tab":
        // No preventDefault: focus moves to the circle inside the keydown so
        // the browser's own traversal resumes from there.
        returnToCircle();
        break;
      default:
        break;
    }
  };

  const onRootBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    if (kbOpen) closeNow();
  };

  return (
    <div
      className={cn("xnc-root", className)}
      data-canvas-control
      data-open={open ? "true" : "false"}
      data-disarmed={disarmed ? "true" : "false"}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onBlur={onRootBlur}
    >
      <div className="xnc-bridge" aria-hidden="true" />

      <div
        className="xnc-arc"
        role="menu"
        aria-label="New chat options"
        aria-hidden={disarmed ? "true" : undefined}
        id={arcId}
      >
        {items.map((item, i) => {
          const { dx, dy } = seat(ARC_SEATS_DEG[item.key]);
          const Glyph = item.icon;
          return (
            <Tooltip key={item.key} delayDuration={400}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  ref={(el) => {
                    satRefs.current[i] = el;
                  }}
                  className="xnc-sat"
                  data-arc={item.key}
                  style={
                    {
                      "--dx": `${dx}px`,
                      "--dy": `${dy}px`,
                      "--k": item.k,
                    } as CSSProperties
                  }
                  aria-label={item.label}
                  onKeyDown={(e) => onSatKeyDown(e, i)}
                  onClick={() => {
                    returnToCircle();
                    onSelect(item.key);
                  }}
                >
                  <Glyph
                    size={CANVAS_MENU_GLYPH}
                    strokeWidth={CANVAS_GLYPH_STROKE}
                    aria-hidden="true"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={12}>
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {/* The circle, last in the DOM so its fill occludes the fan at its
          start positions. Clicking it opens the chat, or closes it while it
          is up (ROUND2 §1) — a state, not a motion: the glyph never morphs.
          It never toggles the fan. Its tooltip stands 16 off to clear the
          shadow a 50px object casts and never coexists with an open fan. */}
      <Tooltip delayDuration={400} open={open ? false : undefined}>
        <TooltipTrigger asChild>
          <button
            ref={btnRef}
            type="button"
            className="xnc-btn"
            data-onboarding-target={onboardingTargetId}
            aria-label={chatOpen ? "Close chat" : "New chat"}
            aria-pressed={chatOpen}
            aria-haspopup={disarmed ? undefined : "menu"}
            aria-expanded={disarmed ? undefined : open}
            aria-controls={disarmed ? undefined : arcId}
            onKeyDown={onBtnKeyDown}
            onClick={onNewChat}
          >
            <MessagesSquare
              size={CANVAS_TILE_GLYPH}
              strokeWidth={CANVAS_GLYPH_STROKE}
              aria-hidden="true"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={16}>
          {chatOpen ? "Close chat" : "New chat"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
